import { TeamGroupColor, TEAM_GROUP_COLORS } from '@numeo/types-main-web-app'

/**
 * A group's badge colour, so "Already in: Yankee" reads as a badge an admin recognises at a glance rather
 * than another line of grey text (ADR-027 §5.1).
 *
 * Three sources, in order:
 *  1. the palette KEY the server assigned at creation — it picks one no live group is using, which is what
 *     makes a tenant's desks visibly distinct;
 *  2. a hex an admin picked by hand — rendered as a tinted pill of that hue;
 *  3. for a group created before colours existed, a palette entry derived from its id — stable across
 *     reloads and independent of list order, so a rename cannot recolour the groups around it.
 *
 * Classes are written out in full because Tailwind only ships classes it can see in the source —
 * `bg-${hue}-100` would compile to nothing.
 */

const PALETTE: Record<TeamGroupColor, string> = {
    sky: 'bg-sky-100 text-sky-700 dark:bg-sky-950 dark:text-sky-300',
    emerald: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300',
    violet: 'bg-violet-100 text-violet-700 dark:bg-violet-950 dark:text-violet-300',
    amber: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300',
    rose: 'bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-300',
    teal: 'bg-teal-100 text-teal-700 dark:bg-teal-950 dark:text-teal-300',
    indigo: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300',
    orange: 'bg-orange-100 text-orange-800 dark:bg-orange-950 dark:text-orange-300',
}

/**
 * Fallback for groups stored before the server assigned colours. Deterministic and order-independent.
 *
 * FNV-1a rather than the usual `hash * 31 + c`: with 31 ≡ 7 (mod 8) a difference in one character cancels a
 * difference in the next, so two ids differing only in their last characters — which ObjectIds created
 * minutes apart routinely do — landed on the same colour.
 */
const paletteIndex = (groupId: string): number => {
    let hash = 0x811c9dc5
    for (let i = 0; i < groupId.length; i += 1) {
        hash ^= groupId.charCodeAt(i)
        hash = Math.imul(hash, 0x01000193) >>> 0
    }
    return hash % TEAM_GROUP_COLORS.length
}

const HEX = /^#[0-9a-fA-F]{6}$/

export interface TeamGroupBadgeStyle {
    className: string
    style?: { backgroundColor: string; color: string }
}

const isPaletteKey = (value: string): value is TeamGroupColor => value in PALETTE

/** Classes (and, for a custom hex, inline colours) for one group's badge or chip. */
export const teamGroupBadgeStyle = (groupId: string, color?: string | null): TeamGroupBadgeStyle => {
    if (color && isPaletteKey(color)) return { className: PALETTE[color] }
    // `1f` ≈ 12% alpha: a tint light enough to read the group's own hue as text on top of it.
    if (color && HEX.test(color)) return { className: '', style: { backgroundColor: `${color}1f`, color } }
    return { className: PALETTE[TEAM_GROUP_COLORS[paletteIndex(groupId)]] }
}
